import * as React from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, useTypedT } from "@racoon/i18n";
import { useToast } from "@racoon/ui";
import { useCancelJobMutation, useForceCompleteJobMutation } from "~/shared/api/jobs/job.grpc";
import {
  jobsForSelectedAction,
  SELECTED_JOBS_UNDO_MS,
  type SelectedJobsAction,
} from "./selected-jobs-action";
import { UndoCountdownBar } from "./undo-countdown-bar";

type Pending = {
  action: SelectedJobsAction;
  routeId: bigint;
  jobIds: bigint[];
  deadline: number;
};

/**
 * Cancels or completes a trip's ticked jobs after a countdown the dispatcher can
 * stop. One action waits at a time; leaving the page drops it unsent. When the
 * countdown ends the jobs are checked again against the trip as it is then, so a
 * job the driver settled meanwhile is not overwritten.
 */
export const useSelectedJobsAction = ({
  routes,
  onSettled,
}: {
  routes: ConnectProto.Route[];
  /** After a run, with the ids of the jobs it settled. */
  onSettled: (routeId: bigint, settledKeys: ReadonlySet<string>) => void;
}) => {
  const t = useTypedT(Namespace.Routes);
  const { toast } = useToast();
  const [cancelJob] = useCancelJobMutation();
  const [forceCompleteJob] = useForceCompleteJobMutation();
  const [pending, setPending] = React.useState<Pending | null>(null);
  const [isRunning, setIsRunning] = React.useState(false);

  const routesRef = React.useRef(routes);
  routesRef.current = routes;

  const schedule = React.useCallback(
    (action: SelectedJobsAction, route: ConnectProto.Route, selectedIds: ReadonlySet<bigint>) => {
      const jobs = jobsForSelectedAction(route, selectedIds);
      if (jobs.length === 0) return;
      setPending({
        action,
        routeId: route.id,
        jobIds: jobs.map((job) => job.id),
        deadline: Date.now() + SELECTED_JOBS_UNDO_MS,
      });
    },
    [],
  );

  const run = React.useCallback(
    async ({ action, routeId, jobIds }: Pending) => {
      const route = routesRef.current.find((r) => String(r.id) === String(routeId));
      const jobs = route ? jobsForSelectedAction(route, new Set(jobIds)) : [];
      if (!route || jobs.length === 0) {
        // The trip closed or left the list, or every job settled meanwhile.
        toast({ title: t("error"), description: t("selectedJobsNothingLeft") });
        return;
      }

      const mutate = action === "cancel" ? cancelJob : forceCompleteJob;
      const settledKeys = new Set<string>();
      setIsRunning(true);
      try {
        // Sequential on purpose: every call can close the same trip.
        for (const job of jobs) {
          await mutate({
            id: BigInt(job.id),
            serviceProviderId: BigInt(job.serviceProviderId),
            serviceReceiverId: BigInt(job.serviceReceiverId),
          }).unwrap();
          settledKeys.add(String(job.id));
        }
        toast({
          title: t("success"),
          description: t(action === "cancel" ? "selectedJobsCancelled" : "selectedJobsCompleted", {
            count: settledKeys.size,
          }),
        });
      } catch (error) {
        // The failed call is reported by the global error toaster; this says
        // how far the run got before it.
        console.error(`Error running ${action} on selected jobs:`, error);
        toast({
          title: t("error"),
          description: t("selectedJobsPartial", { done: settledKeys.size, total: jobs.length }),
          variant: "destructive",
        });
      } finally {
        setIsRunning(false);
        onSettled(route.id, settledKeys);
      }
    },
    [cancelJob, forceCompleteJob, toast, t, onSettled],
  );

  // The timer arms once per pending action; the latest `run` is read when it fires.
  const runRef = React.useRef(run);
  runRef.current = run;

  React.useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => {
      setPending(null);
      void runRef.current(pending);
    }, pending.deadline - Date.now());
    return () => window.clearTimeout(timer);
  }, [pending]);

  const undoBar = pending && (
    <UndoCountdownBar
      message={t(
        pending.action === "cancel" ? "selectedJobsCancelPending" : "selectedJobsCompletePending",
        { count: pending.jobIds.length },
      )}
      deadline={pending.deadline}
      durationMs={SELECTED_JOBS_UNDO_MS}
      onUndo={() => setPending(null)}
    />
  );

  return { schedule, isBusy: pending !== null || isRunning, undoBar };
};
